import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  Check,
  Globe,
  KeyRound,
  Lock,
  MessageSquareText,
  Send,
  Users,
  X,
} from "lucide-react";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import CommunityAvatar from "../components/CommunityAvatar";
import { communitiesAPI, connectionsAPI } from "../services/api";
import { getConnectionLimits } from "../constants/contentLimits";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import "./PublicProfile.css";
import "./DiscoverCommunities.css";
import "./CommunityPublicProfile.css";

const formatListing = (isListed) =>
  isListed ? "Listed in Discover" : "Unlisted";

const CommunityPublicProfile = ({ mode = "view" }) => {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { user, token } = useAuthState();
  const connectionLimits = getConnectionLimits(user);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const [noteStatus, setNoteStatus] = useState(null);
  const isManageMode = mode === "manage";

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await communitiesAPI.getPublicProfile(communityId);
        setProfile(response.data.communityPublicProfile);
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] ||
            "Failed to load community profile",
          "error-warning",
        );
        navigate("/communities");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [addToast, communityId, navigate]);

  if (loading) return <Spinner visible={true} />;

  const canSendNote =
    !isManageMode &&
    token &&
    profile?.ownerUserId &&
    String(profile.ownerUserId) !== String(user?._id || user?.id);
  const noteBodyLimit =
    noteStatus?.noteBodyLimit || connectionLimits.connectionNoteBody;
  const dailyNoteLimit =
    noteStatus?.dailyLimit || connectionLimits.connectionDailyNotes;
  const dailyNotesUsed = noteStatus?.dailyUsage?.mine || 0;
  const dailyLimitReached =
    noteStatus ? !noteStatus.canSend : dailyNotesUsed >= dailyNoteLimit;
  const noteDisabledReason = noteStatus?.disabledReason || "";

  const openNoteModal = async () => {
    setNoteOpen(true);
    if (!profile?.ownerUserId) return;

    try {
      const response = await connectionsAPI.getTargetStatus(profile.ownerUserId);
      setNoteStatus(response.data);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Could not retrieve note allowance",
        "error-warning",
      );
    }
  };

  const handleSendNote = async () => {
    const trimmed = noteBody.trim();
    if (!trimmed) {
      addToast("Note is required", "error-warning");
      return;
    }

    try {
      setNoteSending(true);
      const response = await connectionsAPI.startConnection({
        targetUserId: profile.ownerUserId,
        body: trimmed,
        contextType: "community",
        communityId,
      });
      setNoteStatus((prev) => ({
        ...(prev || {}),
        dailyLimit: response.data.noteLimit || dailyNoteLimit,
        noteBodyLimit,
        dailyUsage: {
          mine: response.data.dailyUsage?.mine || dailyNotesUsed + 1,
        },
        canSend:
          (response.data.dailyUsage?.mine || dailyNotesUsed + 1) <
          (response.data.noteLimit || dailyNoteLimit),
      }));
      addToast("Note sent", "success-info");
      setNoteOpen(false);
      setNoteBody("");
      window.dispatchEvent(new Event("inbox-changed"));
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to send note",
        "error-warning",
      );
    } finally {
      setNoteSending(false);
    }
  };

  const renderCommunityPreviewCard = () => (
    <article className="discover-communities-card community-public-profile-discover-card">
      <div className="discover-communities-card-top">
        <div className="discover-communities-card-badges">
          <span>
            <Users size={14} />
            {profile?.activeMembersCount || 0}
          </span>
          <span>
            <BookOpen size={14} />
            {profile?.lessonsCount || 0}
          </span>
        </div>
        <div className="discover-communities-mode">
          {profile?.joinMode === "direct" ? (
            <span className="discover-communities-mode-pill is-direct">
              <Check size={14} />
              Direct join
            </span>
          ) : (
            <span className="discover-communities-mode-pill is-approval">
              <Lock size={14} />
              Approval required
            </span>
          )}
        </div>
      </div>

      <div className="discover-communities-title-row">
        <CommunityAvatar name={profile?.name} />
        <h2>{profile?.name || "Community"}</h2>
      </div>
      <p
        className={`discover-communities-description ${
          isManageMode ? "" : "community-public-profile-card-description"
        }`}
      >
        {profile?.description || "Listed learning space."}
      </p>

      <div className="discover-communities-meta">
        <span>
          <Globe size={14} />
          {formatListing(profile?.isListed)}
        </span>
        <span>
          <KeyRound size={14} />
          Owner: {profile?.ownerDisplayName || "Asasiasa member"}
        </span>
      </div>

      <div className="discover-communities-actions">
        <span className="discover-communities-state-pill">Preview</span>
      </div>
    </article>
  );

  if (!isManageMode) {
    return (
      <main className="public-profile-page public-profile-view-page community-public-profile-view-page">
        <div className="public-profile-header">
          <div className="public-profile-title-block">
            <div className="public-profile-title-row">
              <h1>{profile?.name || "Community"}</h1>
              {canSendNote ? (
                <Button
                  className="public-profile-note-button"
                  onClick={openNoteModal}
                  title="Send note"
                  aria-label="Send note"
                >
                  <MessageSquareText size={14} />
                </Button>
              ) : null}
            </div>
            <p>Public Asasiasa community profile.</p>
          </div>
        </div>

        {renderCommunityPreviewCard()}

        {noteOpen ? (
          <div
            className="public-profile-note-overlay"
            onClick={() => setNoteOpen(false)}
          >
            <div
              className="public-profile-note-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="public-profile-note-header">
                <div>
                  <h2>Send note</h2>
                  <p>
                    To {profile?.ownerDisplayName || "community owner"} about{" "}
                    {profile?.name || "this community"}
                  </p>
                </div>
                <Button
                  className="public-profile-note-close"
                  onClick={() => setNoteOpen(false)}
                  title="Close"
                  aria-label="Close"
                >
                  <X size={15} />
                </Button>
              </div>
              <textarea
                value={noteBody}
                onChange={(event) => setNoteBody(event.target.value)}
                placeholder="Write a short note..."
                maxLength={noteBodyLimit}
                rows="5"
                className="public-profile-note-textarea"
                disabled={dailyLimitReached}
              />
              <div className="public-profile-note-actions">
                <span className={dailyLimitReached ? "is-limit-reached" : ""}>
                  {noteDisabledReason ||
                    `${noteBody.length}/${noteBodyLimit} · ${dailyNotesUsed}/${dailyNoteLimit} today`}
                </span>
                <Button
                  className="button-btn button-btn-blue"
                  onClick={handleSendNote}
                  disabled={noteSending || dailyLimitReached}
                >
                  <Send size={16} />
                  Send note
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    );
  }

  return (
    <main className="public-profile-page">
      <div className="public-profile-header">
        <div>
          <h1>Community Profile</h1>
        </div>
      </div>

      <div className="public-profile-layout">
        <div className="public-profile-form">
          <section className="public-profile-section community-public-profile-about">
            <h2>About our community</h2>
            <p>
              {profile?.description ||
                "This community has not added an about note yet."}
            </p>
          </section>
        </div>

        <aside className="community-public-profile-side-card">
          {renderCommunityPreviewCard()}
        </aside>
      </div>
    </main>
  );
};

export default CommunityPublicProfile;
